import { useState } from "react";
import type { TerminalAppModel } from "../state/ui-types";
import { transactionUnresolved, type TrackedTransaction } from "../wallet/transaction-tracker";
import { ArrowUpRightIcon } from "./Icons";
import { Overlay } from "./overlays/Overlay";

const labels: Record<TrackedTransaction["phase"], string> = {
  PREPARING: "Preparing transaction",
  AWAITING_SIGNATURE: "Waiting for wallet approval",
  PENDING: "Sent — waiting for confirmation",
  UNCONFIRMED: "Confirmation not yet known",
  CONFIRMED: "Confirmed on-chain",
  ORPHANED: "Previous confirmation removed — review again",
  REVERTED: "Reverted on-chain",
  REJECTED: "Rejected in wallet",
  FAILED: "Preparation failed",
  CANCELLED: "Cancelled before sending",
  REVIEW_REQUIRED: "Receipt needs review",
  SAMPLING_REQUIRED: "Confirmed — sampling unfinished",
  SAMPLING_EXHAUSTED: "Confirmed — operator review required",
  NOT_NEEDED: "Already finalized — no send needed",
};

export function TransactionStatus({ transactions, onCheck, onDismiss }: {
  transactions: TerminalAppModel["transactions"];
  onCheck(id: string): void;
  onDismiss(id: string): void;
}) {
  const [open, setOpen] = useState(false);
  const unresolvedCount = transactions.items.filter(transactionUnresolved).length;
  return <div className="transaction-status">
    <button type="button" className="secondary-action transaction-activity-trigger"
      aria-label="Transaction activities" aria-haspopup="dialog" aria-expanded={open} onClick={() => setOpen(true)}>
      <span>ACTIVITIES</span>
      {transactions.items.length > 0 ? <span className="transaction-activity-count" aria-hidden="true">{transactions.items.length}</span> : null}
      <ArrowUpRightIcon />
    </button>
    {unresolvedCount > 0 ? <span className="transaction-activity-pending" role="status">{unresolvedCount} unresolved</span> : null}
    {!open && transactions.storageWarning ? <p role="alert">{transactions.storageWarning}</p> : null}
    <Overlay open={open} title="Transaction activities" className="transaction-activity-modal" onClose={() => setOpen(false)}>
      {transactions.storageWarning ? <p role="alert">{transactions.storageWarning}</p> : null}
      {transactions.items.length === 0 ? <p className="transaction-activity-empty" role="status">No activity yet.</p> : null}
      <div className="transaction-list">
        {transactions.items.map((item) => <article key={item.id} className={`transaction-entry tx-${item.phase.toLowerCase()}`}>
          <div role="status"><strong>{item.mintIdentity?.route === "REFERRAL" ? `Referral ${item.mintIdentity.milestone} mint` : item.kind === "MINT" ? "Mint" : `${item.kind === "FINALIZE" ? "Finalize" : "Redeem"} NFT #${item.tokenId}`} · {labels[item.phase]}</strong>
            <p>{item.message}</p></div>
          <small>Chain {item.chainId}</small>
          {item.phase === "CONFIRMED" && item.payoutWei ? <p>Verified payout: {item.payoutWei} wei</p> : null}
          {item.hash ? <code className="transaction-hash" aria-label="Transaction hash">{item.hash}</code> : null}
          <div className="transaction-entry-actions">
            {item.hash && item.chainId === 4663 ? <a href={`https://robinhoodchain.blockscout.com/tx/${item.hash}`} target="_blank" rel="noreferrer">View on explorer</a> : null}
            {(item.phase === "UNCONFIRMED" || item.phase === "REVIEW_REQUIRED" || item.kind === "REDEEM" && item.phase === "CONFIRMED") && item.hash ?
              <button type="button" className="secondary-action" onClick={() => onCheck(item.id)}>Check confirmation</button> : null}
            {!transactionUnresolved(item) ?
              <button type="button" className="text-button" onClick={() => onDismiss(item.id)}>Dismiss</button> : null}
            {item.phase === "UNCONFIRMED" && !item.hash ? <button type="button" className="secondary-action"
              onClick={() => onDismiss(item.id)}>I checked wallet activity — dismiss</button> : null}
          </div>
        </article>)}
      </div>
    </Overlay>
  </div>;
}
